import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMatter } from "../../contexts/MatterContext";
import { Heading } from "../../design/heading";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import EditMatterButton from "./components/EditMatterButton";
import LoadingView from "../../components/LoadingView";
import MatterEventsList from "./components/MatterEventsList";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import DeleteMatterButton from "../../components/DeleteMatterButton";

const MatterDetailPage: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>(); // Get the matterId from the route parameters
  const { selectedMatter, setSelectedMatter, error, loading } = useMatter();

  useEffect(() => {
    if (matterId) {
      setSelectedMatter(matterId);
    }
  }, [matterId]);

  if (loading.details) {
    return <LoadingView title="Loading Matter" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedMatter) {
    return <ErrorView error={new Error("Matter not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Matters", href: "/matters" },
          {
            label: selectedMatter.name ?? selectedMatter._id,
            href: `/matter/${selectedMatter._id}`,
          },
        ]}
      />

      <div className="flex flex-col w-full gap-6 md:flex-row">
        <div className="w-full gap-6">
          <div>
            <div className="flex items-end justify-between gap-4">
              <div className="flex gap-4">
                <Heading>{selectedMatter.name}</Heading>
              </div>
              <div>
                <EditMatterButton matter={selectedMatter} />
                <DeleteMatterButton
                  matterId={selectedMatter._id} // Pass the matterId to the DeleteMatterButton
                />
              </div>
            </div>
            <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
              <div className="flex gap-4"></div>
            </div>
          </div>

          <DescriptionList>
            <DescriptionTerm>Identifier</DescriptionTerm>
            <DescriptionDetails>{selectedMatter.name}</DescriptionDetails>
            {selectedMatter.summary && (
              <>
                <DescriptionTerm>Matter Summary</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-wrap" }}>
                  {selectedMatter.summary}
                </DescriptionDetails>
              </>
            )}
            {selectedMatter.importantDetails && (
              <>
                <DescriptionTerm>Important Details</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-wrap" }}>
                  {selectedMatter.importantDetails}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>

          <MatterEventsList matterId={matterId!} />
        </div>
      </div>
    </div>
  );
};

export default MatterDetailPage;
